import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, deleteDoc, doc, query, orderBy, addDoc } from 'firebase/firestore';
import { Edit, Trash2, Plus, RefreshCw } from 'lucide-react';
import './AdminPanel.css';

const GlossaryManager = () => {
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchTerms = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'glossary'), orderBy('term', 'asc'));
            const querySnapshot = await getDocs(q);
            const termsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setTerms(termsData);
        } catch (error) {
            console.error("Error fetching terms: ", error);
            setTerms([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTerms();
    }, []);

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this term?')) {
            try {
                await deleteDoc(doc(db, 'glossary', id));
                setTerms(terms.filter(term => term.id !== id));
            } catch (error) {
                console.error("Error deleting term: ", error);
                alert('Failed to delete term');
            }
        }
    };

    const seedGlossary = async () => {
        if (!window.confirm("WARNING: This will DELETE ALL existing glossary terms and replace them with the 44 Standard BPO Terms. Continue?")) {
            return;
        }

        setLoading(true);
        try {
            // 1. Delete existing terms
            const q = query(collection(db, 'glossary'));
            const querySnapshot = await getDocs(q);
            const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(deletePromises);

            // Helper to create slug
            const createSlug = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');

            // 2. Add new standard BPO terms with Expanded Content
            // Helper to build the rich HTML structure
            const buildDefinition = (data) => `
                <p><strong>Definition:</strong> ${data.snippet}</p>
                
                <h3>How It Works</h3>
                <p>${data.howItWorks}</p>

                <h3>Who Uses It</h3>
                <p>${data.whoUsesIt}</p>

                <h3>When It Applies</h3>
                <p>${data.whenItApplies}</p>

                ${data.costOverview ? `<h3>Cost Overview</h3><p>${data.costOverview}</p>` : ''}

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                    <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
                        <h4 class="text-[#A2CB52] font-bold mb-2">Cost Impact</h4>
                        <p class="text-sm">${data.costImpact}</p>
                    </div>
                    <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
                        <h4 class="text-[#A2CB52] font-bold mb-2">Operational Impact</h4>
                        <p class="text-sm">${data.operationalImpact}</p>
                    </div>
                </div>

                <h3>Risks & Compliance Considerations</h3>
                <ul>
                    ${data.risks.map(risk => `<li>${risk}</li>`).join('')}
                </ul>

                ${data.relatedTerms ? `
                <div class="mt-8 p-6 bg-[#20222D] text-white rounded-xl">
                    <h4 class="text-[#A2CB52] font-bold mb-4">Related Terms</h4>
                    <div class="flex flex-wrap gap-2">
                        ${data.relatedTerms.map(term => `<span class="px-3 py-1 bg-white/10 rounded-full text-sm hover:bg-[#A2CB52] hover:text-[#20222D] transition-colors cursor-pointer">${term}</span>`).join('')}
                    </div>
                </div>` : ''}
            `;

            const rawTerms = [
                // TIER 1 — HIGH COMMERCIAL INTENT
                {
                    term: "Business Process Outsourcing (BPO)",
                    snippet: "Outsourcing specific business functions to an external service provider to reduce costs and improve operational efficiency.",
                    howItWorks: "A company contracts a third-party provider to handle non-core business activities (e.g., customer support, payroll). The provider manages the staff, infrastructure, and delivery of these services.",
                    whoUsesIt: "Startups, SMEs, and Enterprises looking to scale without heavy overhead.",
                    whenItApplies: "When internal resources are stretched, costs are too high, or specialized expertise is needed quickly.",
                    costOverview: "Typically saves 40-70% compared to in-house hiring in Western markets.",
                    costImpact: "Significant reduction in salaries, benefits, office space, and equipment costs.",
                    operationalImpact: "Frees up internal teams to focus on core strategy; adds 24/7 capability.",
                    risks: ["Data security concerns", "Communication barriers", "Quality control challenges"],
                    relatedTerms: ["Offshore Staffing", "KPO", "SLA"],
                    metaTitle: "What is BPO? Business Process Outsourcing Definition & Guide",
                    metaDescription: "Definition of Business Process Outsourcing (BPO): How it works, cost benefits, and operational impact. Learn more."
                },
                {
                    term: "Offshore Staffing",
                    snippet: "Hiring employees located in another country to perform business functions remotely.",
                    howItWorks: "Companies hire dedicated staff in lower-cost regions (e.g., Philippines, India). These employees work exclusively for the client but are legally employed by the offshore provider.",
                    whoUsesIt: "Tech companies, digital agencies, and businesses needing scalable support teams.",
                    whenItApplies: "When you need full-time dedicated staff at a lower cost than local hiring.",
                    costOverview: "Reduces labor costs by up to 70%.",
                    costImpact: "Lower payroll tax, no recruitment fees, reduced overhead.",
                    operationalImpact: "Access to global talent pool; potential time zone advantages.",
                    risks: ["Cultural differences", "Time zone coordination", "Remote management learning curve"],
                    relatedTerms: ["Nearshore Outsourcing", "Staff Augmentation", "EOR"],
                    metaTitle: "Offshore Staffing Definition | BPO Glossary",
                    metaDescription: "What is Offshore Staffing? Understand the definition, benefits, and how it differs from outsourcing."
                },
                {
                    term: "Staff Augmentation",
                    snippet: "Hiring external professionals to temporarily expand internal teams without long-term employment obligations.",
                    howItWorks: "You add skilled professionals to your existing team to fill gaps or specific project needs. You manage them directly.",
                    whoUsesIt: "IT firms, Agencies with seasonal spikes, Project-based businesses.",
                    whenItApplies: "During project crunches, leaves of absence, or when specific skills are missing internally.",
                    costOverview: "Hourly or monthly rates; avoids long-term severance and benefit costs.",
                    costImpact: "Flexible spend; scale up/down as needed without layoff costs.",
                    operationalImpact: "Immediate skill injection; requires strong internal management.",
                    risks: ["Integration time", "Potential lack of company culture alignment"],
                    relatedTerms: ["Managed Services", "Dedicated Remote Team"],
                    metaTitle: "Staff Augmentation Definition & Meaning",
                    metaDescription: "Staff Augmentation defined: How to extend your team temporarily with external exports."
                },
                {
                    term: "Dedicated Remote Team",
                    snippet: "A full-time offshore team working exclusively for one company.",
                    howItWorks: "The provider builds a team that acts as an extension of your local office. You retain control over processes and culture.",
                    whoUsesIt: "SaaS companies, Growing SMEs, E-commerce brands.",
                    whenItApplies: "When you need a permanent department (e.g., Customer Support, Dev Team) at a lower cost.",
                    costOverview: "Fixed monthly fee per employee; predictable budgeting.",
                    costImpact: "Long-term savings; higher ROI than project-based outsourcing.",
                    operationalImpact: "High alignment with company goals; stable workforce.",
                    risks: ["Requires ongoing engagement to maintain culture"],
                    relatedTerms: ["Offshore Staffing", "BPO"],
                    metaTitle: "Dedicated Remote Team Definition",
                    metaDescription: "What is a Dedicated Remote Team? Learn about this long-term offshore staffing model."
                },
                {
                    term: "AI-Augmented Staffing",
                    snippet: "A workforce model combining human professionals with AI tools to increase productivity and reduce operational costs.",
                    howItWorks: "Staff are equipped with AI tools (e.g., auto-drafting emails, code generation) to speed up workflows.",
                    whoUsesIt: "Forward-thinking BPOs, Tech-enabled service providers.",
                    whenItApplies: "When efficiency and speed are critical competitive advantages.",
                    costOverview: "Higher output per head reduces total headcount needs.",
                    costImpact: "More value per dollar spent; reduced need for large volume hiring.",
                    operationalImpact: "Faster turnaround times; higher accuracy.",
                    risks: ["Data privacy with AI tools", "Training requirements"],
                    relatedTerms: ["AI Productivity Layer", "Human-in-the-Loop"],
                    metaTitle: "AI-Augmented Staffing Definition",
                    metaDescription: "Definition of AI-Augmented Staffing: Combining human expertise with AI efficiency."
                },

                // TIER 2 — COMPARISON & VARIATIONS
                {
                    term: "Recruitment Process Outsourcing (RPO)",
                    snippet: "Outsourcing part or all of a company’s recruitment function to a third-party provider.",
                    howItWorks: "The RPO provider manages job posting, sourcing, screening, and sometimes onboarding.",
                    whoUsesIt: "Large enterprises, companies with high-volume hiring needs.",
                    whenItApplies: "During rapid expansion or when internal HR is overwhelmed.",
                    costImpact: "Reduces cost-per-hire and time-to-fill.",
                    operationalImpact: "Streamlines hiring; improves candidate quality.",
                    risks: ["Loss of personal touch in hiring"],
                    relatedTerms: ["Talent Sourcing", "Candidate Vetting"],
                    metaTitle: "What is RPO? Recruitment Process Outsourcing",
                    metaDescription: "RPO Definition: Outsourcing your recruitment function for efficiency and scale."
                },
                {
                    term: "Managed Services",
                    snippet: "Outsourcing ongoing operational functions to a provider responsible for performance and delivery.",
                    howItWorks: "Unlike staffing, the provider manages the *outcome*, not just the people.",
                    whoUsesIt: "Businesses needing IT support, Network management, Security.",
                    whenItApplies: "When you want to hand off responsibility for a function completely.",
                    costImpact: "Predictable monthly costs; shifts CapEx to OpEx.",
                    operationalImpact: "Guaranteed SLAs; reliability.",
                    risks: ["Less control over daily processes", "Vendor lock-in"],
                    relatedTerms: ["SLA", "BPO"],
                    metaTitle: "Managed Services Definition",
                    metaDescription: "Managed Services explained: Outsourcing responsibility for outcomes and performance."
                },
                {
                    term: "Back Office Outsourcing",
                    snippet: "Outsourcing non-customer-facing tasks like accounting, payroll, and data entry.",
                    howItWorks: "Administrative tasks are routed to an offshore team to process securely.",
                    whoUsesIt: "Banks, Insurance, Logistics, Healthcare.",
                    whenItApplies: "When administrative volume slows down core business growth.",
                    costImpact: "High volume savings; converts fixed admin costs to variable.",
                    operationalImpact: "Faster processing times; 24/7 operation potential.",
                    risks: ["Data security", "Accuracy monitoring"],
                    relatedTerms: ["Data Entry", "BPO"],
                    metaTitle: "Back Office Outsourcing Meaning",
                    metaDescription: "Definition of Back Office Outsourcing: Handling admin tasks efficiently."
                },
                {
                    term: "Offshore Accounting Services",
                    snippet: "Hiring overseas accounting professionals to manage bookkeeping, payroll, or financial reporting.",
                    howItWorks: "CPAs or bookkeepers in low-cost regions access your cloud accounting software to manage finances.",
                    whoUsesIt: "Accounting firms, Small businesses.",
                    whenItApplies: "When local CPA rates are prohibitive.",
                    costImpact: "Saves 50%+ on accountant salaries.",
                    operationalImpact: "Books closed faster; improved compliance.",
                    risks: ["Regulatory differences", "Data sensitivity"],
                    relatedTerms: ["BPO", "Cost Overview"],
                    metaTitle: "Offshore Accounting Services Definition",
                    metaDescription: "What are Offshore Accounting Services? Save on bookkeeping and financial reporting."
                },
                {
                    term: "Offshore IT Support",
                    snippet: "Remote technical support services provided by professionals in another country.",
                    howItWorks: "Technicians connect remotely to solve hardware/software issues for your team or customers.",
                    whoUsesIt: "MSPs, Software companies.",
                    whenItApplies: "Need for 24/7 helpdesk coverage.",
                    costImpact: "Affordable 24/7 coverage.",
                    operationalImpact: "Improved customer satisfaction (CSAT) due to availability.",
                    risks: ["Language barriers", "Connectivity stability"],
                    relatedTerms: ["Helpdesk", "Managed Services"],
                    metaTitle: "Offshore IT Support Definition",
                    metaDescription: "Offshore IT Support explained: Providing remote technical assistance globally."
                },
                {
                    term: "Outsourced Payroll Services",
                    snippet: "Third-party management of payroll processing and compliance.",
                    howItWorks: "External provider calculates pay, tax, and benefits.",
                    whoUsesIt: "Companies of all sizes.",
                    whenItApplies: "To ensure tax compliance and reduce admin burden.",
                    costImpact: "Avoids penalties; reduces HR time.",
                    operationalImpact: "Timely payments; compliance accuracy.",
                    risks: ["Data privacy"],
                    relatedTerms: ["BPO", "Back Office"],
                    metaTitle: "Outsourced Payroll Services Definition",
                    metaDescription: "Definition: Outsourced Payroll Services for compliance and efficiency."
                },
                {
                    term: "Virtual Assistant (VA)",
                    snippet: "Remote administrative or operational support provided by skilled offshore professionals.",
                    howItWorks: "A remote worker handles email, scheduling, research, and admin tasks.",
                    whoUsesIt: "Entrepreneurs, Executives, Realtors.",
                    whenItApplies: "When you are bogged down by minor tasks.",
                    costImpact: "Highly affordable ($5-15/hr).",
                    operationalImpact: "Frees up time for high-value work.",
                    risks: ["Task mismatch", "Security"],
                    relatedTerms: ["Staff Augmentation", "BPO"],
                    metaTitle: "Virtual Assistant (VA) Definition",
                    metaDescription: "What is a Virtual Assistant? Remote support for administrative tasks."
                },

                // TIER 3 — METRICS & MODELS
                {
                    term: "Cost Per Hire",
                    snippet: "Total expenses involved in recruiting and onboarding a new employee.",
                    howItWorks: "Calculated by adding advertising, agency fees, and recruiter time divided by hires.",
                    whoUsesIt: "HR Directors, CFOs.",
                    whenItApplies: "Budgeting for growth.",
                    costImpact: "Key metric for ROI analysis.",
                    operationalImpact: "High cost per hire indicates inefficient recruiting.",
                    risks: ["None (Metric)"],
                    relatedTerms: ["RPO", "Time to Fill"],
                    metaTitle: "Cost Per Hire Definition & Formula",
                    metaDescription: "What is Cost Per Hire? Definition and how to calculate it."
                },
                {
                    term: "Total Cost of Employment",
                    snippet: "All direct and indirect costs associated with hiring an employee, including benefits and overhead.",
                    howItWorks: "Includes salary + taxes + benefits + office space + equipment + software.",
                    whoUsesIt: "Finance, HR.",
                    whenItApplies: "Comparing onshore vs offshore savings.",
                    costImpact: "Critical for true cost analysis.",
                    operationalImpact: "Highlights hidden costs of local hiring.",
                    risks: ["Underestimation"],
                    relatedTerms: ["Labor Arbitrage", "Cost Impact"],
                    metaTitle: "Total Cost of Employment Meaning",
                    metaDescription: "Total Cost of Employment: Why salary is just the beginning."
                },
                {
                    term: "Labor Arbitrage",
                    snippet: "Reducing operational costs by hiring talent in lower-cost labor markets.",
                    howItWorks: "Leveraging wage differentials between developed and developing economies.",
                    whoUsesIt: "Multinational corporations, BPOs.",
                    whenItApplies: "Strategic cost reduction planning.",
                    costImpact: "The primary driver of outsourcing savings.",
                    operationalImpact: "Allows reallocation of budget to growth.",
                    risks: ["Wage inflation in source markets"],
                    relatedTerms: ["Offshore Staffing", "BPO"],
                    metaTitle: "Labor Arbitrage Definition",
                    metaDescription: "Definition of Labor Arbitrage in the context of global business."
                },
                {
                    term: "Offshore vs Nearshore Outsourcing",
                    snippet: "Comparison between hiring in distant countries versus geographically closer ones.",
                    howItWorks: "Offshore (far, low cost) vs Nearshore (close, similar time zone, medium cost).",
                    whoUsesIt: "CTOs deciding team location.",
                    whenItApplies: "Choosing between cost efficiency (Offshore) vs collaboration speed (Nearshore).",
                    costImpact: "Offshore is cheaper; Nearshore saves travel/time costs.",
                    operationalImpact: "Nearshore allows real-time collaboration.",
                    risks: ["Talent pool size limitations (Nearshore)"],
                    relatedTerms: ["Offshore", "Nearshore"],
                    metaTitle: "Offshore vs Nearshore Outsourcing Comparison",
                    metaDescription: "Offshore vs Nearshore: Which outsourcing model is right for you?"
                },
                {
                    term: "Offshore vs Outsourcing",
                    snippet: "Clarifies the difference between location-based and function-based outsourcing.",
                    howItWorks: "Outsourcing = *Who* does it (external). Offshore = *Where* they are (abroad).",
                    whoUsesIt: "Students, Business Beginners.",
                    whenItApplies: "Understanding industry terminology.",
                    costImpact: "N/A",
                    operationalImpact: "N/A",
                    risks: ["Confusion leads to poor strategy"],
                    relatedTerms: ["BPO"],
                    metaTitle: "Difference Between Offshore and Outsourcing",
                    metaDescription: "Offshore vs Outsourcing: Understanding the key differences."
                },
                {
                    term: "In-House vs Offshore Staffing",
                    snippet: "Comparing internal hiring with overseas team expansion.",
                    howItWorks: "Weighing control & culture (In-house) vs cost & scale (Offshore).",
                    whoUsesIt: "Founders, HR.",
                    whenItApplies: "Strategic workforce planning.",
                    costImpact: "Offshore offers significant savings.",
                    operationalImpact: "In-house offers easier communication.",
                    risks: ["Culture dilution"],
                    relatedTerms: ["Staff Augmentation"],
                    metaTitle: "In-House vs Offshore Staffing Comparison",
                    metaDescription: "In-House vs Offshore: Pros, Cons, and Cost Analysis."
                },
                {
                    term: "AI-Augmented Staffing vs Traditional BPO",
                    snippet: "Comparing AI-supported workforce models with conventional outsourcing.",
                    howItWorks: "Traditional = Human labor. AI-Augmented = Human + AI efficiency.",
                    whoUsesIt: "Modern businesses.",
                    whenItApplies: "Seeking efficiency beyond just labor arbitrage.",
                    costImpact: "AI models often charge per outcome or reduced FTE rates.",
                    operationalImpact: "Higher quality and consistency with AI.",
                    risks: ["Technology dependence"],
                    relatedTerms: ["AI Staffing"],
                    metaTitle: "AI-Augmented vs Traditional BPO",
                    metaDescription: "The future of outsourcing: AI-Augmented Staffing vs Traditional BPO."
                },
                {
                    term: "Build-Operate-Transfer (BOT)",
                    snippet: "A setup model where a provider builds and manages operations before transferring ownership.",
                    howItWorks: "Provider sets up the office/team -> Runs it -> Client buys it out later.",
                    whoUsesIt: "Large enterprises planning long-term presence.",
                    whenItApplies: "Testing a market before full commitment.",
                    costImpact: "High upfront investment; long-term asset value.",
                    operationalImpact: "Low risk market entry.",
                    risks: ["Transfer complexity"],
                    relatedTerms: ["Dedicated Team"],
                    metaTitle: "Build-Operate-Transfer (BOT) Model Definition",
                    metaDescription: "What is the BOT Model in outsourcing? Build, Operate, Transfer explained."
                },
                {
                    term: "Employer of Record (EOR)",
                    snippet: "A third party legally employing workers on behalf of another company.",
                    howItWorks: "EOR handles legal payroll, taxes, and compliance in the foreign country. You manage the work.",
                    whoUsesIt: "Companies hiring remote talent directly.",
                    whenItApplies: "To hire compliant remote employees without an entity.",
                    costImpact: "Moderate fee per employee; avoids entity setup costs.",
                    operationalImpact: "Enables global hiring instantly.",
                    risks: ["Compliance gaps if EOR fails"],
                    relatedTerms: ["PEO", "Staffing"],
                    metaTitle: "Employer of Record (EOR) Definition",
                    metaDescription: "What is an Employer of Record (EOR)? Global hiring simplified."
                },
                {
                    term: "Independent Contractor vs Offshore Employee",
                    snippet: "Distinction between freelance contracts and structured offshore employment.",
                    howItWorks: "Contractors are flexible but risky. Employees (via EOR/BPO) are stable and compliant.",
                    whoUsesIt: "Legal, HR.",
                    whenItApplies: "Deciding hiring structure.",
                    costImpact: "Contractors have no benefits cost but risk misclassification fines.",
                    operationalImpact: "Employees offer better retention.",
                    risks: ["Misclassification"],
                    relatedTerms: ["EOR", "Compliance"],
                    metaTitle: "Contractor vs Offshore Employee",
                    metaDescription: "Difference between Independent Contractors and Offshore Employees."
                },
                {
                    term: "Service Level Agreement (SLA)",
                    snippet: "A contract defining performance standards between client and service provider.",
                    howItWorks: "Sets targets (e.g., Response Time < 1 hr). Failures incur penalties.",
                    whoUsesIt: "Procurement, Ops Managers.",
                    whenItApplies: "Contracting any outsourced service.",
                    costImpact: "Ensures value for money.",
                    operationalImpact: "Guarantee of quality.",
                    risks: ["Unrealistic targets"],
                    relatedTerms: ["KPI", "Managed Services"],
                    metaTitle: "Service Level Agreement (SLA) Definition",
                    metaDescription: "What is an SLA? Understanding Service Level Agreements."
                },
                {
                    term: "Key Performance Indicators (KPIs)",
                    snippet: "Measurable metrics used to track team performance.",
                    howItWorks: "Metrics like CSAT, AHT, Accuracy Rate used to evaluate success.",
                    whoUsesIt: "Managers.",
                    whenItApplies: "Daily operations management.",
                    costImpact: "drives efficiency.",
                    operationalImpact: "Focuses team on goals.",
                    risks: ["Measuring the wrong things"],
                    relatedTerms: ["SLA"],
                    metaTitle: "KPIs in BPO: Definition & Examples",
                    metaDescription: "Key Performance Indicators (KPIs) defined for outsourcing."
                },
                {
                    term: "Data Protection Compliance",
                    snippet: "Adhering to laws governing personal and business data handling.",
                    howItWorks: "Following GDPR, HIPAA, etc., when data crosses borders.",
                    whoUsesIt: "Legal, compliance officers.",
                    whenItApplies: "Always in outsourcing.",
                    costImpact: "Non-compliance fines are massive.",
                    operationalImpact: "Requires strict protocols.",
                    risks: ["Data breaches", "Fines"],
                    relatedTerms: ["GDPR", "Cybersecurity"],
                    metaTitle: "Data Protection Compliance in Outsourcing",
                    metaDescription: "Data Protection Compliance defined: Keeping data safe offshore."
                },
                {
                    term: "Intellectual Property (IP) Protection",
                    snippet: "Safeguards ensuring ownership of work created by outsourced staff.",
                    howItWorks: "Clauses in contracts ensuring client owns all code/content created.",
                    whoUsesIt: "Software companies, R&D.",
                    whenItApplies: "Outsourcing development or design.",
                    costImpact: " Protects asset value.",
                    operationalImpact: "Requires secure environments.",
                    risks: ["IP Theft"],
                    relatedTerms: ["NDA", "Legal"],
                    metaTitle: "IP Protection in Outsourcing Definition",
                    metaDescription: "How to protect Intellectual Property when outsourcing."
                },
                {
                    term: "Non-Disclosure Agreement (NDA)",
                    snippet: "A legal contract protecting confidential information.",
                    howItWorks: "Prevents staff/provider from sharing your trade secrets.",
                    whoUsesIt: "All businesses.",
                    whenItApplies: "Before sharing any internal data.",
                    costImpact: "Legal safety net.",
                    operationalImpact: "Standard procedure.",
                    risks: ["Enforceability across borders"],
                    relatedTerms: ["Legal", "IP Protection"],
                    metaTitle: "Non-Disclosure Agreement (NDA) Meaning",
                    metaDescription: "What is an NDA? Non-Disclosure Agreements for outsourcing."
                },
                {
                    term: "Employment Classification",
                    snippet: "Legal determination of worker status (employee vs contractor).",
                    howItWorks: "Criteria like 'control' and 'financial independence' determine status.",
                    whoUsesIt: "Legal, HR.",
                    whenItApplies: "Hiring direct contractors.",
                    costImpact: "Misclassification leads to back-taxes.",
                    operationalImpact: "Determines management style.",
                    risks: ["Legal lawsuits"],
                    relatedTerms: ["EOR", "Contractor"],
                    metaTitle: "Employment Classification Definition",
                    metaDescription: "Worker Classification: Employee vs Contractor risks."
                },
                {
                    term: "Offshore Hiring Risks",
                    snippet: "Potential operational, legal, and cultural risks when hiring overseas.",
                    howItWorks: "risk assessment of political stability, infrastructure, etc.",
                    whoUsesIt: "Risk Management.",
                    whenItApplies: "Market entry.",
                    costImpact: "Mitigation costs vs potential loss.",
                    operationalImpact: "Need for contingency plans.",
                    risks: ["Stability", "Currency fluctuation"],
                    relatedTerms: ["Risk Management"],
                    metaTitle: "Offshore Hiring Risks Explained",
                    metaDescription: "Understanding the risks of offshore hiring and how to mitigate them."
                },
                {
                    term: "Cybersecurity in Outsourcing",
                    snippet: "Security protocols protecting business systems when using offshore teams.",
                    howItWorks: "Firewalls, VPNs, Endpoint protection, Zero-trust architecture.",
                    whoUsesIt: "IT Security.",
                    whenItApplies: "Remote access setup.",
                    costImpact: "Security investment prevents costly breaches.",
                    operationalImpact: "May add friction to workflow (2FA etc).",
                    risks: ["Hacking", "Social Engineering"],
                    relatedTerms: ["Data Compliance"],
                    metaTitle: "Cybersecurity in Outsourcing Definition",
                    metaDescription: "Best practices for Cybersecurity in Outsourcing."
                },
                {
                    term: "Workforce Scalability",
                    snippet: "Ability to expand or reduce staffing levels efficiently.",
                    howItWorks: "Using flexible staffing models to match demand.",
                    whoUsesIt: "Seasonal businesses, Startups.",
                    whenItApplies: "Growth spurts or downturns.",
                    costImpact: "Prevents overstaffing costs.",
                    operationalImpact: "Agility in market response.",
                    risks: ["Quality flux during rapid scale"],
                    relatedTerms: ["Staff Augmentation"],
                    metaTitle: "Workforce Scalability Definition",
                    metaDescription: "What is Workforce Scalability? Managing growth efficiently."
                },
                {
                    term: "Time Zone Management",
                    snippet: "Coordinating global teams across different geographic time zones.",
                    howItWorks: "Using overlap hours or async workflows.",
                    whoUsesIt: "Remote Managers.",
                    whenItApplies: "Global teams.",
                    costImpact: "Shift differentials.",
                    operationalImpact: "24/7 productivity potential.",
                    risks: ["Burnout"],
                    relatedTerms: ["Remote Management"],
                    metaTitle: "Time Zone Management for Remote Teams",
                    metaDescription: "How to manage Time Zones in offshore teams."
                },
                {
                    term: "Remote Team Management",
                    snippet: "Strategies for supervising distributed employees.",
                    howItWorks: "Utilizing tools like Slack, Jira, Zoom and clear policies.",
                    whoUsesIt: "Team Leads.",
                    whenItApplies: "Managing any off-site staff.",
                    costImpact: "Tool costs.",
                    operationalImpact: "Critical for retention and output.",
                    risks: ["Disengagement"],
                    relatedTerms: ["Workforce Scalability"],
                    metaTitle: "Remote Team Management Definition",
                    metaDescription: "Definition of Remote Team Management best practices."
                },
                {
                    term: "Talent Sourcing",
                    snippet: "Identifying and attracting qualified candidates.",
                    howItWorks: "Searching pipelines, LinkedIn, and databases.",
                    whoUsesIt: "Recruiters.",
                    whenItApplies: "Hiring phase.",
                    costImpact: "Sourcing tool costs.",
                    operationalImpact: "Quality of pipeline.",
                    risks: ["Slow time to hire"],
                    relatedTerms: ["RPO"],
                    metaTitle: "Talent Sourcing Definition",
                    metaDescription: "What is Talent Sourcing? Finding the best candidates."
                },
                {
                    term: "Candidate Vetting",
                    snippet: "Screening and evaluating candidates before hiring.",
                    howItWorks: "Background checks, skill tests, interviews.",
                    whoUsesIt: "Hiring Managers.",
                    whenItApplies: "Selection phase.",
                    costImpact: "Screening costs.",
                    operationalImpact: "Reduces bad hire rate.",
                    risks: ["False positives"],
                    relatedTerms: ["Recruitment"],
                    metaTitle: "Candidate Vetting Process Definition",
                    metaDescription: "Candidate Vetting explained: Ensuring quality hires."
                },
                {
                    term: "Onboarding Process",
                    snippet: "Structured integration of new employees into operations.",
                    howItWorks: "Training, setup, introductions.",
                    whoUsesIt: "HR, Team Leads.",
                    whenItApplies: "First 30-90 days.",
                    costImpact: "Training time costs.",
                    operationalImpact: "Time to productivity.",
                    risks: ["Early attrition"],
                    relatedTerms: ["HR"],
                    metaTitle: "Onboarding Process Definition",
                    metaDescription: "What is the Onboarding Process? Integrating new hires."
                },
                {
                    term: "Performance Monitoring",
                    snippet: "Tracking productivity and output of remote staff.",
                    howItWorks: "Software tracking, regular reviews.",
                    whoUsesIt: "Managers.",
                    whenItApplies: "Ongoing operations.",
                    costImpact: "Monitoring tool costs.",
                    operationalImpact: "Accountability.",
                    risks: ["Micromanagement perception"],
                    relatedTerms: ["KPIs"],
                    metaTitle: "Performance Monitoring Definition",
                    metaDescription: "Performance Monitoring for remote teams defined."
                },
                {
                    term: "Workflow Automation",
                    snippet: "Using software or AI to automate repetitive processes.",
                    howItWorks: "Scripting tasks like data entry or routing.",
                    whoUsesIt: "Ops Managers.",
                    whenItApplies: "Optimizing efficiency.",
                    costImpact: "Reduces labor hours.",
                    operationalImpact: "Consistency and speed.",
                    risks: ["System failure"],
                    relatedTerms: ["AI"],
                    metaTitle: "Workflow Automation Definition",
                    metaDescription: "Workflow Automation: Reducing manual work."
                },
                {
                    term: "Human-in-the-Loop",
                    snippet: "AI systems that require human oversight for quality and compliance.",
                    howItWorks: "AI does the draft; Human reviews and approves.",
                    whoUsesIt: "AI Ops.",
                    whenItApplies: "Complex tasks.",
                    costImpact: "Balanced cost.",
                    operationalImpact: "Safety + Speed.",
                    risks: ["Human fatigue"],
                    relatedTerms: ["AI-Augmented Staffing"],
                    metaTitle: "Human-in-the-Loop (HITL) Definition",
                    metaDescription: "Human-in-the-Loop AI models explained."
                },
                {
                    term: "Productivity Optimization",
                    snippet: "Improving output through systems, technology, and staffing strategies.",
                    howItWorks: "Analyzing bottlenecks and fixing them.",
                    whoUsesIt: "COOs.",
                    whenItApplies: "Scaling.",
                    costImpact: "Higher margins.",
                    operationalImpact: "Better throughput.",
                    risks: ["Burnout"],
                    relatedTerms: ["Operational Efficiency"],
                    metaTitle: "Productivity Optimization Definition",
                    metaDescription: "What is Productivity Optimization in business?"
                },
                {
                    term: "Operational Efficiency",
                    snippet: "Ratio of output to input in business resources.",
                    howItWorks: "Doing more with less waste.",
                    whoUsesIt: "Management.",
                    whenItApplies: "Always.",
                    costImpact: "Directs profitability.",
                    operationalImpact: "Streamlined processes.",
                    risks: ["Cutting corners"],
                    relatedTerms: ["Productivity"],
                    metaTitle: "Operational Efficiency Definition",
                    metaDescription: "Operational Efficiency defined: Maximizing output."
                },
                {
                    term: "Compliance Audits",
                    snippet: "Formal reviews ensuring outsourcing practices meet regulatory standards.",
                    howItWorks: "External or internal auditors check processes against laws.",
                    whoUsesIt: "Compliance Teams.",
                    whenItApplies: "Annually or pre-contract.",
                    costImpact: "Audit fees.",
                    operationalImpact: "Ensures license to operate.",
                    risks: ["Finding violations"],
                    relatedTerms: ["Data Protection"],
                    metaTitle: "Compliance Audits in Outsourcing",
                    metaDescription: "What are Compliance Audits? Ensuring adherence to standards."
                },
                {
                    term: "Cost Overview",
                    snippet: "Analysis of complete financial implications of a business decision.",
                    howItWorks: "Reviewing CapEx and OpEx.",
                    whoUsesIt: "CFOs.",
                    whenItApplies: "Strategic planning.",
                    costImpact: "Allows informed decisions.",
                    operationalImpact: "Budget alignment.",
                    risks: ["Inaccurate data"],
                    relatedTerms: ["TCO"],
                    metaTitle: "Cost Overview Definition",
                    metaDescription: "Cost Overview meaning and importance."
                }
            ];

            const addPromises = rawTerms.map(item => {
                const termData = {
                    term: item.term,
                    definition: buildDefinition(item),
                    slug: createSlug(item.term.split('(')[0].trim()),
                    metaTitle: item.metaTitle || `${item.term} Definition | BPO Glossary`,
                    metaDescription: item.metaDescription || `Definition of ${item.term}. ${item.snippet}`,
                    published: true,
                    createdAt: new Date().toISOString()
                };
                return addDoc(collection(db, 'glossary'), termData);
            });

            await Promise.all(addPromises);

            alert('Glossary has been reset and populated with 44 Expanded Terms!');
            fetchTerms();

        } catch (error) {
            console.error("Error seeding glossary:", error);
            alert('Error seeding glossary: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="panel active">
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">Content</div>
                    <h1>Glossary <span>.</span></h1>
                </div>
                <div className="page-hdr-actions">
                    <Link to="/admin/dashboard" className="btn btn-ghost" style={{ borderColor: 'var(--border)', color: 'var(--text2)' }}>
                        Dashboard
                    </Link>
                    <button className="btn btn-ghost" onClick={seedGlossary}>
                        <RefreshCw size={14} /> Reset & Seed Glossary
                    </button>
                    <Link to="/admin/glossary/new" className="btn btn-primary">
                        <Plus size={14} style={{ marginRight: '4px' }} /> New Term
                    </Link>
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="s-label">Total Terms</div>
                    <div className="s-val">{terms.length}</div>
                </div>
                <div className="stat-card green">
                    <div className="s-label">Published</div>
                    <div className="s-val">{terms.filter(t => t.published !== false).length}</div>
                </div>
            </div>

            <div className="tbl-wrap">
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>Loading terms...</div>
                ) : terms.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                        <p style={{ color: 'var(--muted)', marginBottom: '10px' }}>No glossary terms found.</p>
                        <Link to="/admin/glossary/new" style={{ color: 'var(--accent)', fontSize: '13px' }}>Add your first term</Link>
                    </div>
                ) : (
                    <div className="tbl-scroll">
                        <table>
                            <thead>
                                <tr>
                                    <th>Term</th>
                                    <th>Definition</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {terms.map((item) => (
                                    <tr key={item.id}>
                                        <td style={{ fontWeight: 500 }}>{item.term}</td>
                                        <td className="muted" style={{ maxWidth: '300px' }}>
                                            <div dangerouslySetInnerHTML={{ __html: item.definition ? item.definition.replace(/<[^>]*>?/gm, '').substring(0, 50) + '...' : '' }} />
                                        </td>
                                        <td>
                                            <span className={`badge ${item.published !== false ? 'badge-organic' : 'badge-bing'}`}>
                                                {item.published !== false ? 'Published' : 'Draft'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="action-btns">
                                                <Link to={`/admin/glossary/edit/${item.id}`} className="btn-ghost" style={{ padding: '4px', display: 'inline-flex' }}>
                                                    <Edit size={14} />
                                                </Link>
                                                <button onClick={() => handleDelete(item.id)} className="btn-ghost" style={{ padding: '4px', color: 'var(--accent3)', borderColor: 'var(--accent3-dim)' }}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GlossaryManager;
